<app-sidebar>
  <div class="users-page">
    <div class="page-header">
      <h1>Manage Users</h1>
    </div>

    <div *ngIf="loading" class="users-loading-state">
      <mat-spinner diameter="50"></mat-spinner>
      <p>Loading users...</p>
    </div>

    <div class="users-card" *ngIf="!loading">
      <div class="users-toolbar">
        <mat-form-field appearance="outline" class="users-filter-field">
          <mat-label>User Role</mat-label>
          <mat-select [(ngModel)]="selectedRoles" (selectionChange)="applyFilters()" multiple>
            <mat-option *ngFor="let option of roleFilterOptions" [value]="option.value">
              {{ option.label }}
            </mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline" class="users-filter-field">
          <mat-label>User Status</mat-label>
          <mat-select [(ngModel)]="selectedStatuses" (selectionChange)="applyFilters()" multiple>
            <mat-option *ngFor="let option of statusFilterOptions" [value]="option.value">
              {{ option.label }}
            </mat-option>
          </mat-select>
        </mat-form-field>

        <button mat-button type="button" class="clear-filters-button" *ngIf="hasActiveFilters()" (click)="clearFilters()">
          Clear filters
        </button>

        <button mat-raised-button color="primary" class="add-user-button" (click)="addUser()">
          <mat-icon>add</mat-icon>
          Invite User
        </button>
      </div>

      <mat-table [dataSource]="dataSource">
        <ng-container matColumnDef="name">
          <mat-header-cell *matHeaderCellDef>Name</mat-header-cell>
          <mat-cell *matCellDef="let user">
            {{ user.name || 'N/A' }}
            <span *ngIf="isCurrentUser(user)" class="you-indicator">(You)</span>
          </mat-cell>
        </ng-container>

        <ng-container matColumnDef="surname">
          <mat-header-cell *matHeaderCellDef>Surname</mat-header-cell>
          <mat-cell *matCellDef="let user">{{ user.surname || 'N/A' }}</mat-cell>
        </ng-container>

        <ng-container matColumnDef="account">
          <mat-header-cell *matHeaderCellDef>Account</mat-header-cell>
          <mat-cell *matCellDef="let user">
            <span class="role-chip-group">
              <span>{{ getAccountLabel(user) }}</span>
              <mat-chip *ngIf="isTrialUser(user)" color="accent">Trial</mat-chip>
            </span>
          </mat-cell>
        </ng-container>

        <ng-container matColumnDef="email">
          <mat-header-cell *matHeaderCellDef>Email</mat-header-cell>
          <mat-cell *matCellDef="let user">{{ user.email || 'N/A' }}</mat-cell>
        </ng-container>

        <ng-container matColumnDef="role">
          <mat-header-cell *matHeaderCellDef>Role</mat-header-cell>
          <mat-cell *matCellDef="let user">
            <span class="role-chip-group">
              <mat-chip [color]="user.role === 'admin' ? 'primary' : 'accent'">
                {{ user.role }}
              </mat-chip>
              <mat-chip *ngIf="user.isBillingOwner === true" color="primary">
                billing
              </mat-chip>
            </span>
            <mat-icon
              *ngIf="isInvitePending(user)"
              class="invite-pending-icon"
              [matTooltip]="invitePendingTooltip"
              [attr.aria-label]="invitePendingTooltip">
              schedule
            </mat-icon>
            <mat-icon
              *ngIf="hasPromoCampaign(user)"
              class="campaign-icon"
              [matTooltip]="promoCampaignTooltip(user)"
              [attr.aria-label]="promoCampaignTooltip(user)">
              campaign
            </mat-icon>
            <mat-icon
              *ngIf="isPendingPayment(user)"
              class="payment-due-icon"
              [matTooltip]="paymentDueTooltip"
              [attr.aria-label]="paymentDueTooltip">
              payments
            </mat-icon>
          </mat-cell>
        </ng-container>

        <ng-container matColumnDef="credits">
          <mat-header-cell *matHeaderCellDef>Credits (used / allocated)</mat-header-cell>
          <mat-cell *matCellDef="let user">
            {{ user.creditsUsedThisPeriod ?? 0 }} / {{ user.creditAllocation ?? 0 }}
          </mat-cell>
        </ng-container>

        <ng-container matColumnDef="enabled">
          <mat-header-cell *matHeaderCellDef>Enabled</mat-header-cell>
          <mat-cell *matCellDef="let user">
            <mat-checkbox
              [checked]="user.enabled"
              (change)="toggleEnabled(user)"
              [disabled]="isCurrentUser(user) || isPending(user) || isInvitePending(user)"
              [color]="'primary'">
            </mat-checkbox>
          </mat-cell>
        </ng-container>

        <ng-container matColumnDef="actions">
          <mat-header-cell *matHeaderCellDef>Actions</mat-header-cell>
          <mat-cell *matCellDef="let user" class="action-cell">
            <button
              mat-icon-button
              [matMenuTriggerFor]="userActionsMenu"
              [matMenuTriggerData]="{ user: user }"
              matTooltip="User actions"
              aria-label="Open user actions menu">
              <mat-icon>more_vert</mat-icon>
            </button>
          </mat-cell>
        </ng-container>

        <mat-menu #userActionsMenu="matMenu">
          <ng-template matMenuContent let-user="user">
            <button
              mat-menu-item
              (click)="viewIdDocument(user)"
              [disabled]="isInvitePending(user) || (!user.idDocumentPath && !user.ffcDocumentPath)">
              <mat-icon>badge</mat-icon>
              <span>View Legal Info</span>
            </button>

            <button
              *ngIf="isAwaitingApproval(user)"
              mat-menu-item
              (click)="approveUser(user)"
              [disabled]="!canActionApproval(user)">
              <mat-icon>check_circle</mat-icon>
              <span>Approve</span>
            </button>

            <button
              *ngIf="isAwaitingApproval(user)"
              mat-menu-item
              (click)="rejectUser(user)"
              [disabled]="!canActionApproval(user)">
              <mat-icon>cancel</mat-icon>
              <span>Reject</span>
            </button>

            <button
              *ngIf="!isPending(user) && !isRejected(user) && !isInvitePending(user)"
              mat-menu-item
              (click)="editUser(user)">
              <mat-icon>edit</mat-icon>
              <span>Edit User</span>
            </button>

            <button
              *ngIf="!isPending(user) && !isRejected(user) && !isInvitePending(user)"
              mat-menu-item
              (click)="viewAuditLog(user)">
              <mat-icon>history_edu</mat-icon>
              <span>Audit Log</span>
            </button>

            <button
              *ngIf="canMasquerade(user)"
              mat-menu-item
              (click)="masqueradeAs(user)">
              <mat-icon>visibility</mat-icon>
              <span>Masquerade as User</span>
            </button>

            <button
              *ngIf="!isPending(user) && !isRejected(user) && !isInvitePending(user)"
              mat-menu-item
              (click)="resetPassword(user)">
              <mat-icon>lock_reset</mat-icon>
              <span>Reset Password</span>
            </button>

            <button
              *ngIf="canResendInvite(user)"
              mat-menu-item
              (click)="resendInvite(user)">
              <mat-icon>forward_to_inbox</mat-icon>
              <span>Resend Invite</span>
            </button>

            <button
              mat-menu-item
              (click)="deleteUser(user)"
              [disabled]="isCurrentUser(user)">
              <mat-icon>delete</mat-icon>
              <span>Delete User</span>
            </button>
          </ng-template>
        </mat-menu>

        <mat-header-row *matHeaderRowDef="displayedColumns"></mat-header-row>
        <mat-row
          *matRowDef="let row; columns: displayedColumns"
          [class.pending-approval-row]="isAwaitingApproval(row)">
        </mat-row>
      </mat-table>
    </div>
  </div>
</app-sidebar>

